import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { faMessageXmark } from '@fortawesome/pro-thin-svg-icons';

import { PlatformApp } from '@typings';
import { PATH } from '@constants';
import {
  platformAppsInstancesSelector,
  platformAppsLoadersSelector,
} from '@selectors';
import { useSelector } from '@hooks';

import { ChaseSpinner, Link } from '@components';
import { EmptyContent } from '@components/Ui';
import { PlatformAppInstalledItem } from '@components/Ui/PlatformApps';

export const PlatformAppsInstalledPage: React.FC = () => {
  const { appName } = useParams();

  const { instancesFetched, instancesLoading } = useSelector(
    platformAppsLoadersSelector,
  );
  const instances = useSelector(platformAppsInstancesSelector);

  const filteredInstances = useMemo(() => {
    if (!appName) {
      return instances;
    }
    return instances.filter((instance) => instance.templateName === appName);
  }, [appName, instances]);

  const makeInstance = ({
    id,
    name,
    displayName,
    state,
    templateName,
    templateVersion,
  }: PlatformApp.Instance) => {
    return (
      <PlatformAppInstalledItem
        key={id}
        id={id}
        name={name}
        displayName={displayName}
        state={state}
        templateName={templateName}
        templateVersion={templateVersion}
      />
    );
  };

  if (instancesLoading || !instancesFetched) {
    return (
      <div className="flex justify-center">
        <ChaseSpinner color="primary" className="absolute mt-40 h-14 w-14" />
      </div>
    );
  }

  if (!filteredInstances.length) {
    return (
      <EmptyContent
        icon={faMessageXmark}
        title="No Apps Installed"
        text="It looks like you haven’t installed any apps yet. Visit the Apps page to explore and install apps to get started"
        className="mt-40"
      >
        <Link to={PATH.APPS} className="capitalize">
          Explore apps
        </Link>
      </EmptyContent>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-4 hdp:grid-cols-2">
      {filteredInstances.map(makeInstance)}
    </div>
  );
};
